import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { mediaUrl, kr } from "@/lib/api";
import { useDevice } from "@/components/Device";

const shapeCls = {
  square: "aspect-square max-w-sm mx-auto",
  rect: "aspect-[16/6]",
  circle: "aspect-square max-w-sm mx-auto rounded-full",
  trapez: "aspect-[16/7] clip-trapez",
};

// ---------- Image / Diasshow slide (full-bleed banner, text overlays, clickable) ----------
function TextOverlay({ t }) {
  const style = {
    position: "absolute",
    left: (t.x ?? 8) + "%",
    top: (t.y ?? 10) + "%",
    width: t.w ? t.w + "%" : "auto",
    fontSize: (t.fontSize || 40) + "px",
    fontFamily: t.font || undefined,
    fontWeight: t.weight || 800,
    color: t.color || "#ffffff",
    textAlign: t.align || "left",
    opacity: t.opacity ?? 1,
    lineHeight: t.lineHeight || 1.1,
    letterSpacing: t.letterSpacing ? t.letterSpacing + "px" : undefined,
    transform: t.rotation ? `rotate(${t.rotation}deg)` : undefined,
    background: t.bg || undefined,
    padding: t.bg ? "0.25em 0.6em" : undefined,
    borderRadius: t.bg ? "0.6rem" : undefined,
    textShadow: t.shadow ? "0 2px 14px rgba(0,0,0,0.55)" : undefined,
    whiteSpace: "pre-wrap",
  };
  return <div style={style} className="pointer-events-none select-none" data-testid="slide-text-overlay">{t.text}</div>;
}

function ImageSlideShow({ slide, playing = true }) {
  const navigate = useNavigate();
  const device = useDevice();
  const images = slide.images || [];
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const touch = useRef(null);

  useEffect(() => { setI(0); }, [slide.id, images.length]);
  useEffect(() => {
    if (!playing || !slide.autoplay || images.length <= 1 || paused) return;
    const t = setInterval(() => setI((p) => (p + 1) % images.length), slide.duration || 4000);
    return () => clearInterval(t);
  }, [playing, slide.autoplay, images.length, slide.duration, paused, slide.id]);

  if (!images.length) {
    return <div className="flex aspect-[16/6] w-full items-center justify-center rounded-3xl bg-secondary text-sm text-muted-foreground" data-testid="slide-empty-images">Ingen billeder i diasshowet</div>;
  }

  const aspect = device === "mobile" ? (slide.aspect_mobile || "4 / 5") : (slide.aspect || "16 / 6");
  const img = images[i % images.length];
  const go = (im) => {
    const l = im?.link || slide.destination;
    if (!l) return;
    if (/^https?:/.test(l)) window.location.href = l; else navigate(l);
  };
  const next = () => setI((p) => (p + 1) % images.length);
  const prev = () => setI((p) => (p - 1 + images.length) % images.length);
  const variants = (slide.transition === "slide")
    ? { initial: { opacity: 0, x: 90 }, animate: { opacity: 1, x: 0 }, exit: { opacity: 0, x: -90 } }
    : { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } };

  return (
    <div
      data-testid={`slide-${slide.id}`}
      className="group relative w-full overflow-hidden rounded-3xl bg-foreground/5"
      style={{ aspectRatio: aspect }}
      onMouseEnter={() => slide.pause_on_hover && setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={(e) => { touch.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => { if (touch.current == null) return; const dx = e.changedTouches[0].clientX - touch.current; if (dx < -40) next(); else if (dx > 40) prev(); touch.current = null; }}
    >
      <AnimatePresence mode="wait">
        <motion.div key={i} variants={variants} initial="initial" animate="animate" exit="exit" transition={{ duration: slide.transition_speed || 0.6, ease: "easeOut" }} className="absolute inset-0">
          <img
            src={mediaUrl(img.src)}
            alt=""
            onClick={() => go(img)}
            className={`absolute inset-0 h-full w-full object-cover ${(img.link || slide.destination) ? "cursor-pointer" : ""}`}
          />
          {(img.texts || []).map((t) => <TextOverlay key={t.id} t={t} />)}
        </motion.div>
      </AnimatePresence>

      {slide.arrows && images.length > 1 && (
        <>
          <button onClick={prev} data-testid="slide-arrow-prev" className="absolute left-3 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/40 text-white opacity-0 backdrop-blur transition-opacity group-hover:opacity-100 hover:bg-black/60"><ChevronLeft className="h-5 w-5" /></button>
          <button onClick={next} data-testid="slide-arrow-next" className="absolute right-3 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/40 text-white opacity-0 backdrop-blur transition-opacity group-hover:opacity-100 hover:bg-black/60"><ChevronRight className="h-5 w-5" /></button>
        </>
      )}
      {slide.dots && images.length > 1 && (
        <div className="absolute bottom-3 left-1/2 z-10 flex -translate-x-1/2 gap-1.5">
          {images.map((_, idx) => <button key={idx} onClick={() => setI(idx)} data-testid={`slide-dot-${idx}`} className={`h-2 rounded-full transition-all ${idx === i ? "w-6 bg-white" : "w-2 bg-white/50"}`} />)}
        </div>
      )}
    </div>
  );
}

// ---------- Product slide (existing, unchanged behaviour) ----------
export function SlideShow({ slide, playing = true }) {
  if (slide.type === "image") return <ImageSlideShow slide={slide} playing={playing} />;
  return <ProductSlideShow slide={slide} playing={playing} />;
}

function ProductSlideShow({ slide, playing = true }) {
  const navigate = useNavigate();
  const products = slide.products || [];
  const [i, setI] = useState(0);

  useEffect(() => { setI(0); }, [slide.id, products.length]);

  useEffect(() => {
    if (!playing || products.length <= 1) return;
    const t = setInterval(() => setI((p) => (p + 1) % products.length), slide.duration || 3500);
    return () => clearInterval(t);
  }, [playing, products.length, slide.duration, slide.id]);

  if (!products.length) return null;

  const p = products[i % products.length];
  const c = slide.content || {};
  const dir = slide.direction === "right" ? -1 : 1;
  const variants = {
    fade: { initial: { opacity: 0, scale: 0.92 }, animate: { opacity: 1, scale: 1 }, exit: { opacity: 0, scale: 0.92 } },
    spin: { initial: { opacity: 0, rotate: -160, scale: 0.6 }, animate: { opacity: 1, rotate: 0, scale: 1 }, exit: { opacity: 0, rotate: 160, scale: 0.6 } },
    slide: { initial: { opacity: 0, x: 120 * dir }, animate: { opacity: 1, x: 0 }, exit: { opacity: 0, x: -120 * dir } },
  }[slide.animation || "fade"];

  const isCircle = slide.shape === "circle";
  const go = () => {
    if (slide.destination) {
      if (/^https?:/.test(slide.destination)) window.location.href = slide.destination;
      else navigate(slide.destination);
    } else navigate(`/produkter/${p.slug}`);
  };

  return (
    <div
      onClick={go}
      data-testid={`slide-${slide.id}`}
      className={`group relative w-full cursor-pointer overflow-hidden border border-border bg-gradient-to-br from-primary/10 via-secondary to-background shadow-md transition-shadow hover:shadow-[0_16px_44px_rgba(236,30,121,0.22)] ${isCircle ? "rounded-full" : "rounded-3xl"} ${shapeCls[slide.shape] || shapeCls.square}`}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={i}
          variants={variants}
          initial="initial" animate="animate" exit="exit"
          transition={{ duration: 0.5, ease: "easeOut" }}
          className={`absolute inset-0 flex items-center justify-center gap-4 p-5 sm:p-8 ${isCircle ? "flex-col text-center" : ""}`}
        >
          <img src={mediaUrl(p.main_image)} alt={p.name} className={`object-contain drop-shadow-xl ${isCircle ? "h-[45%] w-auto" : "h-full max-h-[80%] w-auto max-w-[45%]"}`} />
          <div className={isCircle ? "max-w-[80%]" : "max-w-[48%]"}>
            {c.name !== false && <p className="text-base font-extrabold leading-tight sm:text-2xl">{p.name}</p>}
            {c.text && <p className="mt-1 line-clamp-2 text-xs text-muted-foreground sm:text-sm">{c.text}</p>}
            <div className={`mt-2 flex items-baseline gap-2 ${isCircle ? "justify-center" : ""}`}>
              {c.price !== false && <span className="text-lg font-black text-primary sm:text-3xl">{kr(p.effective_price)}</span>}
              {c.offer !== false && p.on_sale && p.former_price && <span className="text-xs text-muted-foreground line-through sm:text-sm">{kr(p.former_price)}</span>}
              {c.discount !== false && p.on_sale && p.discount_percent > 0 && <span className="rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold text-primary-foreground sm:text-xs">-{p.discount_percent}%</span>}
            </div>
            {c.cta_text && <span className="mt-3 inline-block rounded-full bg-primary px-5 py-2 text-xs font-bold text-primary-foreground sm:text-sm">{c.cta_text}</span>}
          </div>
        </motion.div>
      </AnimatePresence>
      {products.length > 1 && !isCircle && (
        <div className="absolute bottom-2 left-1/2 z-10 flex -translate-x-1/2 gap-1">
          {products.map((_, idx) => <span key={idx} className={`h-1.5 rounded-full transition-all ${idx === i ? "w-4 bg-primary" : "w-1.5 bg-border"}`} />)}
        </div>
      )}
    </div>
  );
}
